import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Smartphone,
  Apple,
  Monitor,
  Share,
  Plus,
  Download,
  CheckCircle2,
  ChevronDown,
  Heart,
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * /install-app — step-by-step guide for installing the PWA as a home-screen app.
 *
 * Detects the user's platform and defaults to the matching tab. Linked from
 * the welcome email and from the navbar / member area.
 */
function detectPlatform() {
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

const PLATFORMS = [
  { id: 'ios', label: 'iPhone / iPad', icon: Apple },
  { id: 'android', label: 'Android', icon: Smartphone },
  { id: 'desktop', label: 'Počítač', icon: Monitor },
];

const STEPS = {
  ios: [
    {
      icon: Smartphone,
      title: 'Otvorte stránku v Safari',
      body: 'Návod funguje len v prehliadači Safari. Ak ste v Chrome alebo inom, kliknite na ikonu zdieľania v ňom a zvoľte „Otvoriť v Safari".',
    },
    {
      icon: Share,
      title: 'Ťuknite na tlačidlo Zdieľať',
      body: 'Je to ikonka štvorca so šípkou nahor — nájdete ju v dolnej lište Safari uprostred (alebo vpravo hore na iPade).',
    },
    {
      icon: Plus,
      title: 'Vyberte „Pridať na plochu"',
      body: 'V zozname možností scrollujte trochu nadol a ťuknite na možnosť „Pridať na plochu" (Add to Home Screen).',
    },
    {
      icon: CheckCircle2,
      title: 'Potvrďte „Pridať"',
      body: 'V pravom hornom rohu ťuknite na „Pridať". Ikonka srdiečka v oranžovej farbe sa objaví na vašej domovskej obrazovke.',
    },
    {
      icon: Heart,
      title: 'Hotovo! Otvorte appku z plochy',
      body: 'Od teraz si appku otvárate jedným ťuknutím — bez adresného riadku, bez rušivých prvkov.',
    },
  ],
  android: [
    {
      icon: Smartphone,
      title: 'Otvorte stránku v Chrome',
      body: 'Návod funguje najlepšie v prehliadači Google Chrome. Otvorte si v ňom klub.varimesrdcom.sk.',
    },
    {
      icon: Download,
      title: 'Vyhľadajte ikonku Inštalácia',
      body: 'Buď uvidíte automatický banner v dolnej časti („Pridať aplikáciu na mobil"), alebo ťuknite na 3 bodky vpravo hore a vyberte „Inštalovať aplikáciu".',
    },
    {
      icon: CheckCircle2,
      title: 'Potvrďte „Inštalovať"',
      body: 'Chrome sa opýta, či chcete appku nainštalovať — kliknite na „Inštalovať". Ikonka sa pridá medzi vaše appky.',
    },
    {
      icon: Heart,
      title: 'Hotovo! Otvorte z domovskej obrazovky',
      body: 'Appku nájdete medzi ostatnými appkami (Whatsapp, Instagram...). Otvára sa fullscreen ako natívna appka.',
    },
  ],
  desktop: [
    {
      icon: Monitor,
      title: 'Otvorte stránku v Chrome alebo Edge',
      body: 'Návod funguje v Google Chrome a Microsoft Edge na Windows, Mac alebo Linux.',
    },
    {
      icon: Download,
      title: 'Kliknite na ikonku Inštalácia v adresnom riadku',
      body: 'Vpravo v adresnom riadku (vedľa záložky/hviezdičky) sa objaví malá ikonka monitora so šípkou nadol. Kliknite na ňu.',
    },
    {
      icon: CheckCircle2,
      title: 'Potvrďte „Inštalovať"',
      body: 'V dialógu kliknite na „Inštalovať". Aplikácia sa otvorí v samostatnom okne a pridá sa medzi vaše aplikácie.',
    },
    {
      icon: Heart,
      title: 'Hotovo! Pripnite si ju',
      body: 'Appku môžete pripnúť na Lištu úloh (Windows) alebo Dock (Mac), aby ste ju mali vždy poruke.',
    },
  ],
};

export default function InstallAppPage() {
  const { language } = useAuth();
  const [active, setActive] = useState('ios');
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setActive(detectPlatform());
    // Detect already-installed (standalone display mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setInstalled(isStandalone);
  }, []);

  const steps = STEPS[active] || STEPS.ios;

  return (
    <div className="min-h-screen bg-cream-subtle py-12" data-testid="install-app-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-primary/15 items-center justify-center mb-5">
            <Heart className="w-8 h-8 text-primary fill-primary" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-text-heading mb-3">
            {language === 'sk'
              ? 'Pridajte si appku na mobil'
              : 'Přidejte si appku na mobil'}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {language === 'sk'
              ? 'Pridajte si "S Luciou v kuchyni" priamo na domovskú obrazovku — otvára sa rýchlo, ako bežná aplikácia, bez adresného riadku. Inštalácia trvá 20 sekúnd.'
              : 'Přidejte si "S Luciou v kuchyni" přímo na domovskou obrazovku — otevírá se rychle, jako běžná aplikace, bez adresního řádku. Instalace trvá 20 sekund.'}
          </p>
        </div>

        {/* Already installed banner */}
        {installed && (
          <div className="mb-8 rounded-xl bg-secondary/15 border border-secondary/40 p-4 flex items-center gap-3" data-testid="already-installed">
            <CheckCircle2 className="w-5 h-5 text-secondary-foreground shrink-0" />
            <p className="text-sm text-text-heading">
              {language === 'sk'
                ? 'Appku už máte nainštalovanú — práve ju používate. ❤'
                : 'Appku už máte nainstalovanou — právě ji používáte. ❤'}
            </p>
          </div>
        )}

        {/* Platform tabs */}
        <div className="bg-white border border-border/60 rounded-2xl p-1.5 grid grid-cols-3 gap-1 mb-8" data-testid="platform-tabs">
          {PLATFORMS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActive(id)}
              className={cn(
                'flex flex-col sm:flex-row items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-medium transition-all',
                active === id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-heading hover:bg-muted/50'
              )}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Steps */}
        <ol className="space-y-4" data-testid="install-steps">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <li
                key={i}
                className="bg-white border border-border/60 rounded-2xl p-5 sm:p-6 flex gap-4"
              >
                <div className="shrink-0 flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/15 text-primary font-serif font-bold text-lg flex items-center justify-center">
                    {i + 1}
                  </div>
                  <Icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="font-medium text-text-heading mb-1.5">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* FAQ */}
        <details className="mt-8 bg-white border border-border/60 rounded-2xl p-5 group">
          <summary className="cursor-pointer flex items-center justify-between font-medium text-text-heading">
            <span>Časté otázky</span>
            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-4 space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-text-heading mb-1">Je to natívna aplikácia z App Store / Google Play?</p>
              <p>Nie, je to PWA (Progressive Web App) — nainštaluje sa priamo z prehliadača bez nutnosti prejsť cez obchod s aplikáciami. Funguje takmer rovnako ako natívna appka.</p>
            </div>
            <div>
              <p className="font-medium text-text-heading mb-1">Môžem ju používať bez internetu?</p>
              <p>Appka sa otvorí aj offline, ale recepty, ebooky a videokurzy potrebujú pripojenie. Po pripojení sa všetko načíta normálne.</p>
            </div>
            <div>
              <p className="font-medium text-text-heading mb-1">Koľko miesta zaberie v telefóne?</p>
              <p>Veľmi málo — približne 5 MB. Žiadne reklamy, žiadne sledovanie.</p>
            </div>
            <div>
              <p className="font-medium text-text-heading mb-1">Ako appku odinštalujem?</p>
              <p>Rovnako ako bežnú appku — podržte ikonku na domovskej obrazovke a vyberte „Odstrániť aplikáciu".</p>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
